import React, { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, RotateCw } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

const cardMotion = {
  hidden: { opacity: 0, y: 24 },
  show: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: index * 0.08, ease: "easeOut" },
  }),
};

const badgeStyles = {
  "Sur demande": "border-[#bfdbfe] bg-[#eff6ff] text-[#1d4ed8]",
  "Bientôt disponible": "border-[#fde68a] bg-[#fefce8] text-[#b45309]",
  Gratuit: "border-[#bbf7d0] bg-[#ecfdf5] text-[#166534]",
};

const ctaStyles = {
  primary: "bg-[#0f766e] text-white hover:bg-[#149289]",
  secondary: "border-[#d8ece4] bg-white text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]",
  disabled: "bg-slate-200 text-slate-600 hover:bg-slate-200 cursor-default",
};

export const LogicielShowcaseCard = ({ logiciel, index }) => {
  const [showDetails, setShowDetails] = useState(false);
  const panelMinHeight = useMemo(() => (logiciel.mode === "journey" ? "31rem" : "34rem"), [logiciel.mode]);

  return (
    <motion.div custom={index} initial="hidden" viewport={{ once: true, amount: 0.18 }} whileInView="show" variants={cardMotion}>
      <Card className="group overflow-hidden rounded-[2.2rem] border border-[#d9e7d5]/90 bg-[linear-gradient(180deg,_rgba(255,255,255,0.96)_0%,_rgba(248,252,243,0.98)_100%)] shadow-[0_30px_86px_-56px_rgba(15,118,110,0.32)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_40px_98px_-52px_rgba(15,118,110,0.36)]" data-testid={`logiciel-card-${logiciel.slug}`}>
        <CardContent className="p-0">
          <div className="relative overflow-hidden" style={{ minHeight: panelMinHeight }}>
            <AnimatePresence initial={false} mode="wait">
              {!showDetails ? (
                <motion.div
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  className="p-7 sm:p-8"
                  data-testid={`logiciel-card-front-${logiciel.slug}`}
                  exit={{ opacity: 0, x: -26, y: -6 }}
                  initial={{ opacity: 0, x: 26, y: 8 }}
                  key={`${logiciel.slug}-front`}
                  transition={{ duration: 0.62, ease: "easeInOut" }}
                >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid={`logiciel-card-eyebrow-${logiciel.slug}`}>Logiciel Bimcubes</div>
                    <h3 className="bim-display mt-4 text-[1.55rem] font-semibold leading-tight text-slate-950" data-testid={`logiciel-card-title-${logiciel.slug}`}>{logiciel.name}</h3>
                  </div>
                  <Badge className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${badgeStyles[logiciel.badge] || badgeStyles["Bientôt disponible"]}`} data-testid={`logiciel-card-badge-${logiciel.slug}`} variant="outline">
                    {logiciel.badge}
                  </Badge>
                </div>

                <div className="mt-6 overflow-hidden rounded-[1.7rem] border border-white/70 bg-[linear-gradient(180deg,_rgba(255,255,255,0.9)_0%,_rgba(240,253,244,0.98)_100%)] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]" data-testid={`logiciel-card-visual-${logiciel.slug}`}>
                  <div className="relative flex h-40 items-center justify-center overflow-hidden rounded-[1.2rem] border border-[#d8ece4] bg-white/95 px-4 py-5 sm:px-6" data-testid={`logiciel-card-logo-stage-${logiciel.slug}`}>
                    <img
                      alt={logiciel.logoAlt || logiciel.name}
                      className="h-full w-full object-contain object-center"
                      data-testid={`logiciel-card-logo-${logiciel.slug}`}
                      src={logiciel.logoUrl}
                    />
                  </div>
                </div>

                <p className="mt-6 text-sm leading-8 text-slate-600 sm:text-base" data-testid={`logiciel-card-description-${logiciel.slug}`}>{logiciel.description}</p>

                <div className="mt-7 rounded-[1.5rem] border border-[#d8ece4] bg-white/90 p-5" data-testid={`logiciel-card-price-panel-${logiciel.slug}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Prix</div>
                  <div className="mt-3 text-2xl font-black text-slate-950" data-testid={`logiciel-card-price-${logiciel.slug}`}>{logiciel.price}</div>
                </div>

                <Button className={`mt-7 h-12 w-full rounded-full ${ctaStyles.secondary}`} data-testid={`logiciel-card-toggle-button-${logiciel.slug}`} onClick={() => setShowDetails(true)} type="button" variant="outline">
                  Voir les fonctionnalités
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                </motion.div>
              ) : (
                <motion.div
                  animate={{ opacity: 1, x: 0, y: 0 }}
                  className="p-7 sm:p-8"
                  data-testid={`logiciel-card-back-${logiciel.slug}`}
                  exit={{ opacity: 0, x: 26, y: 6 }}
                  initial={{ opacity: 0, x: 26, y: 8 }}
                  key={`${logiciel.slug}-back`}
                  transition={{ duration: 0.62, ease: "easeInOut" }}
                >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0f766e]" data-testid={`logiciel-card-back-eyebrow-${logiciel.slug}`}>Fonctionnalités</div>
                    <h3 className="bim-display mt-4 text-[1.55rem] font-semibold leading-tight text-slate-950" data-testid={`logiciel-card-back-title-${logiciel.slug}`}>{logiciel.name}</h3>
                  </div>
                  <Button className="rounded-full border-[#d8ece4] bg-white text-[#0f766e] hover:bg-[#ecfdf5] hover:text-[#0f766e]" data-testid={`logiciel-card-back-button-${logiciel.slug}`} onClick={() => setShowDetails(false)} type="button" variant="outline">
                    <RotateCw className="mr-2 h-4 w-4" />
                    Retour
                  </Button>
                </div>

                {logiciel.mode === "journey" ? (
                  <div className="mt-6 space-y-4" data-testid={`logiciel-card-journey-${logiciel.slug}`}>
                    {logiciel.journey.map((step, stepIndex) => (
                      <div className="flex items-center gap-4 rounded-[1.3rem] border border-[#d8ece4] bg-white/90 p-4 shadow-[0_16px_38px_-30px_rgba(15,118,110,0.2)]" data-testid={`logiciel-card-journey-step-${logiciel.slug}-${stepIndex + 1}`} key={`${logiciel.slug}-journey-${stepIndex + 1}`}>
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0f766e] text-sm font-bold text-white">{stepIndex + 1}</div>
                        <div className="text-sm font-medium leading-7 text-slate-700">{step}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="mt-6 space-y-3" data-testid={`logiciel-card-features-${logiciel.slug}`}>
                    {logiciel.features.map((feature, featureIndex) => (
                      <li className="flex gap-3 rounded-[1.2rem] border border-[#d8ece4] bg-white/92 px-4 py-3 text-sm leading-7 text-slate-700 shadow-[0_16px_36px_-32px_rgba(15,118,110,0.18)]" data-testid={`logiciel-card-feature-${logiciel.slug}-${featureIndex + 1}`} key={`${logiciel.slug}-feature-${featureIndex + 1}`}>
                        <Check className="mt-1 h-4 w-4 shrink-0 text-[#0f766e]" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-6 rounded-[1.5rem] border border-[#d8ece4] bg-[#f8fcf3] p-5" data-testid={`logiciel-card-back-price-panel-${logiciel.slug}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Prix</div>
                  <div className="mt-3 text-2xl font-black text-slate-950" data-testid={`logiciel-card-back-price-${logiciel.slug}`}>{logiciel.price}</div>
                </div>

                <Button className={`mt-7 h-12 w-full rounded-full ${ctaStyles[logiciel.ctaTone]}`} data-testid={`logiciel-card-cta-button-${logiciel.slug}`} onClick={logiciel.onAction} type="button" variant={logiciel.ctaTone === "secondary" ? "outline" : "default"}>
                  {logiciel.ctaLabel}
                </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};